import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ArrowLeft, BookOpen, List, Layers } from 'lucide-react';
import guides from '../data/guides';
import './GuideViewer.css';

function StepMedia({ step, index }) {
  const source = step.media?.src || step.gif;
  const type = step.media?.type || (source && /\.(mp4|webm|mov)$/i.test(source) ? 'video' : 'image');

  if (!source) {
    return (
      <div className="gv-placeholder">
        <BookOpen size={index == null ? 48 : 40} strokeWidth={1.2} />
        <span>Guide recording will go here</span>
        {index != null && <span className="gv-placeholder-hint">Step {index + 1}</span>}
      </div>
    );
  }

  if (type === 'video') {
    return (
      <video
        src={source}
        className="gv-media"
        autoPlay
        muted
        loop
        playsInline
        controls
        preload="metadata"
        aria-label={step.title}
      />
    );
  }

  return <img src={source} alt={step.title} className="gv-media" />;
}

function GuideViewer() {
  const { guideId } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [mode, setMode] = useState('scroll'); // 'scroll' | 'steps'
  const scrollSectionsRef = useRef([]);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [activeScrollStep, setActiveScrollStep] = useState(0);
  const guide = guides.find((g) => g.id === guideId);

  // Reset everything when guide changes
  useEffect(() => {
    setStep(0);
    setScrollProgress(0);
    setActiveScrollStep(0);
    scrollSectionsRef.current = [];
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [guideId]);

  // ── Scroll mode: intersection observer for reveal animations ──
  useEffect(() => {
    if (mode !== 'scroll') return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('gv-visible');
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -60px 0px' },
    );

    const els = document.querySelectorAll('.gv-scroll-step');
    els.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [mode, guideId]);

  // ── Scroll mode: progress bar + active step tracking ──
  useEffect(() => {
    if (mode !== 'scroll') return;

    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = docHeight > 0 ? Math.min((scrollTop / docHeight) * 100, 100) : 0;
      setScrollProgress(progress);

      // Find which step is most centered in viewport
      const sections = scrollSectionsRef.current;
      let closest = 0;
      let closestDist = Infinity;
      const viewCenter = window.innerHeight / 2;

      sections.forEach((el, i) => {
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const elCenter = rect.top + rect.height / 2;
        const dist = Math.abs(elCenter - viewCenter);
        if (dist < closestDist) {
          closestDist = dist;
          closest = i;
        }
      });

      setActiveScrollStep(closest);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [mode, guideId]);

  // ── Steps mode: keyboard navigation ──
  const handleKey = useCallback(
    (e) => {
      if (!guide || mode !== 'steps') return;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        setStep((s) => (s < guide.steps.length - 1 ? s + 1 : s));
      }
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        setStep((s) => (s > 0 ? s - 1 : s));
      }
    },
    [guide, mode],
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [handleKey]);

  // ── Not found ──
  if (!guide) {
    return (
      <div className="gv">
        <div className="gv-empty">
          <h2>Guide not found</h2>
          <p>The guide you're looking for doesn't exist.</p>
          <Link to="/landing#guides" className="gv-back-link">
            <ArrowLeft size={16} /> Back to Guides
          </Link>
        </div>
      </div>
    );
  }

  const total = guide.steps.length;
  const stepsProgress = mode === 'steps' ? ((step + 1) / total) * 100 : scrollProgress;

  const guideIndex = guides.findIndex((g) => g.id === guideId);
  const nextGuide = guideIndex < guides.length - 1 ? guides[guideIndex + 1] : null;

  const scrollToStep = (i) => {
    const el = scrollSectionsRef.current[i];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  return (
    <div className={`gv ${mode === 'scroll' ? 'gv-scroll-mode' : 'gv-steps-mode'}`}>
      {/* ── Header ── */}
      <header className="gv-header">
        <Link to="/landing#guides" className="gv-back-link">
          <ArrowLeft size={16} />
          <span>All Guides</span>
        </Link>

        <div className="gv-header-center">
          <span className="gv-tag">{guide.tag}</span>
          <h1 className="gv-title">{guide.title}</h1>
        </div>

        <div className="gv-header-right">
          <div className="gv-mode-toggle">
            <button
              className={`gv-mode-btn ${mode === 'scroll' ? 'active' : ''}`}
              onClick={() => setMode('scroll')}
              title="Scroll view"
            >
              <List size={15} />
            </button>
            <button
              className={`gv-mode-btn ${mode === 'steps' ? 'active' : ''}`}
              onClick={() => setMode('steps')}
              title="Step-by-step view"
            >
              <Layers size={15} />
            </button>
          </div>
          {mode === 'steps' && (
            <span className="gv-step-count">{step + 1} / {total}</span>
          )}
        </div>
      </header>

      {/* ── Progress bar ── */}
      <div className="gv-progress-track">
        <div className="gv-progress-fill" style={{ width: `${stepsProgress}%` }} />
      </div>

      {/* ═══════════════ SCROLL MODE ═══════════════ */}
      {mode === 'scroll' && (
        <div key={guideId}>
          {/* Side step indicator */}
          <div className="gv-scroll-indicator">
            {guide.steps.map((_, i) => (
              <button
                key={i}
                className={`gv-scroll-dot ${i === activeScrollStep ? 'active' : ''} ${i < activeScrollStep ? 'completed' : ''}`}
                onClick={() => scrollToStep(i)}
                aria-label={`Jump to step ${i + 1}`}
              />
            ))}
          </div>

          {/* Hero intro */}
          <section className="gv-scroll-hero">
            <div className="gv-scroll-hero-inner">
              <span className="gv-tag">{guide.tag}</span>
              <h1 className="gv-scroll-hero-title">{guide.title}</h1>
              <p className="gv-scroll-hero-desc">{guide.desc}</p>
              <div className="gv-scroll-meta">
                <span>{total} steps</span>
                <span className="gv-scroll-meta-dot" />
                <span>~{total * 2} min read</span>
              </div>
            </div>
          </section>

          {/* Steps */}
          <div className="gv-scroll-steps">
            {guide.steps.map((s, i) => (
              <section
                key={i}
                className={`gv-scroll-step ${i % 2 === 1 ? 'gv-scroll-step-reverse' : ''}`}
                ref={(el) => (scrollSectionsRef.current[i] = el)}
              >
                {/* Connector line */}
                {i < total - 1 && <div className="gv-scroll-connector" />}

                <div className="gv-scroll-step-inner">
                  {/* Visual */}
                  <div className="gv-scroll-visual">
                    <StepMedia step={s} />
                  </div>

                  {/* Text */}
                  <div className="gv-scroll-text">
                    <div className="gv-scroll-num">{i + 1}</div>
                    <h2 className="gv-scroll-step-title">{s.title}</h2>
                    <p className="gv-scroll-step-desc">{s.description}</p>
                  </div>
                </div>
              </section>
            ))}
          </div>

          {/* Bottom navigation */}
          <div className="gv-scroll-footer">
            {nextGuide ? (
              <Link to={`/guide/${nextGuide.id}`} className="gv-scroll-next-guide">
                <span className="gv-scroll-next-label">Next Guide</span>
                <span className="gv-scroll-next-title">{nextGuide.title}</span>
                <ChevronRight size={20} />
              </Link>
            ) : (
              <Link to="/landing#guides" className="gv-scroll-next-guide">
                <span className="gv-scroll-next-label">Finished</span>
                <span className="gv-scroll-next-title">Back to All Guides</span>
                <ChevronRight size={20} />
              </Link>
            )}
          </div>
        </div>
      )}

      {/* ═══════════════ STEPS MODE ═══════════════ */}
      {mode === 'steps' && (
        <main className="gv-main" key={guideId}>
          <div className="gv-content" key={step}>
            <div className="gv-visual">
              <StepMedia step={guide.steps[step]} index={step} />
            </div>

            <div className="gv-text">
              <div className="gv-step-badge">Step {step + 1}</div>
              <h2 className="gv-step-title">{guide.steps[step].title}</h2>
              <p className="gv-step-desc">{guide.steps[step].description}</p>
            </div>
          </div>

          <div className="gv-dots">
            {guide.steps.map((_, i) => (
              <button
                key={i}
                className={`gv-dot ${i === step ? 'active' : ''} ${i < step ? 'completed' : ''}`}
                onClick={() => setStep(i)}
                aria-label={`Go to step ${i + 1}`}
              />
            ))}
          </div>

          <div className="gv-nav">
            <button className="gv-nav-btn" onClick={() => setStep(step - 1)} disabled={step === 0}>
              <ChevronLeft size={18} />
              <span>Previous</span>
            </button>

            {step < total - 1 ? (
              <button className="gv-nav-btn gv-nav-primary" onClick={() => setStep(step + 1)}>
                <span>Next</span>
                <ChevronRight size={18} />
              </button>
            ) : nextGuide ? (
              <button
                className="gv-nav-btn gv-nav-primary"
                onClick={() => navigate(`/guide/${nextGuide.id}`)}
              >
                <span>Next Guide: {nextGuide.title}</span>
                <ChevronRight size={18} />
              </button>
            ) : (
              <Link to="/landing#guides" className="gv-nav-btn gv-nav-primary">
                <span>Back to Guides</span>
              </Link>
            )}
          </div>
        </main>
      )}
    </div>
  );
}

export default GuideViewer;
